import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../supabaseClient'
import {
  Search, ExternalLink, Mail, Phone, Globe,
  Building2, MapPin, FolderKanban, UserCheck,
} from 'lucide-react'

const Avatar = ({ profile }) => {
  const displayName = profile.nickname || profile.full_name || '?'
  const letter = displayName[0]?.toUpperCase() ?? '?'
  const avatarUrl = profile.avatar_url ? `${profile.avatar_url}?t=0` : null
  return (
    <div className="w-14 h-14 rounded-full bg-emerald-600 flex items-center justify-center text-white font-bold text-xl overflow-hidden flex-shrink-0">
      {avatarUrl
        ? <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover" />
        : <span>{letter}</span>}
    </div>
  )
}

const ensureHttp = (url) => {
  if (!url) return url
  return /^https?:\/\//i.test(url) ? url : `https://${url}`
}

const InfoRow = ({ icon: Icon, value, href }) => {
  if (!value) return null
  const content = (
    <span className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-emerald-400 transition-colors truncate">
      <Icon size={12} className="flex-shrink-0 text-gray-600" />
      <span className="truncate">{value}</span>
      {href && <ExternalLink size={10} className="flex-shrink-0 opacity-50" />}
    </span>
  )
  if (href) return <a href={ensureHttp(href)} target="_blank" rel="noopener noreferrer">{content}</a>
  if (value.includes('@')) return <a href={`mailto:${value}`}>{content}</a>
  return <div>{content}</div>
}

const ClienteCard = ({ cliente, progCount }) => {
  const nome = cliente.nickname || cliente.full_name || 'Cliente'
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 flex flex-col gap-4 hover:border-gray-700 transition-colors">
      {/* Header */}
      <div className="flex items-start gap-3">
        <Avatar profile={cliente} />
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-semibold text-base truncate">{nome}</h3>
          {cliente.company && (
            <p className="text-emerald-400 text-sm font-medium mt-0.5 flex items-center gap-1">
              <Building2 size={12} /> {cliente.company}
            </p>
          )}
          {progCount > 0 && (
            <span className="inline-flex items-center gap-1 text-xs text-violet-400 bg-violet-400/10 px-2 py-0.5 rounded-full mt-1">
              <FolderKanban size={10} /> {progCount} {progCount === 1 ? 'progetto' : 'progetti'}
            </span>
          )}
        </div>
      </div>

      {/* Bio */}
      {cliente.bio && (
        <p className="text-gray-400 text-sm leading-relaxed line-clamp-2">{cliente.bio}</p>
      )}

      {/* Info */}
      <div className="flex flex-col gap-1.5">
        <InfoRow icon={Mail} value={cliente.email_contatto} />
        <InfoRow icon={Phone} value={cliente.phone} />
        <InfoRow icon={Globe} value={cliente.website} href={cliente.website} />
        <InfoRow icon={MapPin} value={cliente.city} />
      </div>
    </div>
  )
}

/* ─── Main ─── */
const Clienti = () => {
  const [clienti, setClienti] = useState([])
  const [progCounts, setProgCounts] = useState({})
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')

  const fetchClienti = useCallback(async () => {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('role', 'client')
      .order('full_name', { ascending: true })
    setClienti(data || [])
    setLoading(false)

    // Conta progetti per ogni cliente
    if (data?.length) {
      const ids = data.map(c => c.id)
      const { data: members } = await supabase
        .from('project_members')
        .select('user_id, project_id')
        .in('user_id', ids)
      if (members) {
        const counts = {}
        members.forEach(m => { counts[m.user_id] = (counts[m.user_id] || 0) + 1 })
        setProgCounts(counts)
      }
    }
  }, [])

  useEffect(() => { fetchClienti() }, [fetchClienti])

  const filtered = clienti.filter(c => {
    const q = query.toLowerCase()
    return (
      (c.full_name || '').toLowerCase().includes(q) ||
      (c.nickname || '').toLowerCase().includes(q) ||
      (c.company || '').toLowerCase().includes(q) ||
      (c.email_contatto || '').toLowerCase().includes(q) ||
      (c.city || '').toLowerCase().includes(q)
    )
  })

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Database Clienti</h1>
          <p className="text-gray-500 text-sm mt-1 flex items-center gap-1.5">
            <UserCheck size={13} />
            {clienti.length} {clienti.length === 1 ? 'cliente registrato' : 'clienti registrati'} · visibile solo agli admin
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Cerca per nome, azienda, email, città..."
          className="w-full bg-gray-900 border border-gray-800 rounded-xl pl-9 pr-4 py-2.5 text-white placeholder-gray-600 text-sm focus:outline-none focus:border-violet-500 transition-colors"
        />
      </div>

      {/* Contenuto */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-12 text-center">
          <UserCheck size={36} className="text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">
            {query ? 'Nessun risultato.' : 'Nessun cliente registrato ancora.'}
          </p>
          <p className="text-gray-600 text-xs mt-1">
            I clienti appaiono qui quando si iscrivono tramite link di invito.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(c => (
            <ClienteCard
              key={c.id}
              cliente={c}
              progCount={progCounts[c.id] || 0}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default Clienti
